import { BaseProps } from '../types';
import { Box, ClickAwayListener, Tooltip, TooltipProps, Typography, TypographyProps } from '@mui/material';
import { useState, useCallback, useMemo } from 'react';

/** The truncation variant determines which part of the text is visible */
export type TruncateVariant = 'hide-end' | 'hide-start' | 'hide-middle';

type Props = BaseProps & {
  /** The text to truncate */
  text: string;
  /** Maximum number of characters to display before truncation */
  maxChars: number;
  /** Which part of the text to hide when truncating */
  variant: TruncateVariant;
  /** Typography variant to use for the text */
  typographyVariant?: TypographyProps['variant'];
  /** Additional props to pass to the Tooltip component */
  tooltipProps?: Partial<Omit<TooltipProps, 'title' | 'children'>>;
  /** Additional props to pass to the Typography component */
  typographyProps?: Omit<TypographyProps, 'variant' | 'children'>;
};

export type TruncateProps = Props;

const ELLIPSIS = '...';

type TruncateOptions = {
  text: string;
  maxChars: number;
  variant: TruncateVariant;
};

/**
 * Truncates text based on the specified variant
 * @param options - The truncation options
 * @returns The truncated text with ellipsis
 */
const truncateText = (options: TruncateOptions): string => {
  const { text, maxChars, variant } = options;

  if (text.length <= maxChars) {
    return text;
  }

  switch (variant) {
    case 'hide-end':
      // Show first maxChars characters + ellipsis
      return `${text.slice(0, maxChars)}${ELLIPSIS}`;

    case 'hide-start':
      // Show ellipsis + last maxChars characters
      return `${ELLIPSIS}${text.slice(-maxChars)}`;

    case 'hide-middle': {
      // Split maxChars between start and end
      const startChars = Math.ceil(maxChars / 2);
      const endChars = Math.floor(maxChars / 2);
      return `${text.slice(0, startChars)}${ELLIPSIS}${text.slice(-endChars)}`;
    }

    default:
      return text;
  }
};

/**
 * A component that truncates text to a specified maximum number of characters,
 * with configurable truncation position (start, end, or middle).
 * Shows the full text in a tooltip on hover (desktop) or click (mobile).
 *
 * @example
 * ```tsx
 * // Truncate at end: "1234..."
 * <Truncate text="1234567890" maxChars={4} variant="hide-end" />
 *
 * // Truncate at start: "...7890"
 * <Truncate text="1234567890" maxChars={4} variant="hide-start" />
 *
 * // Truncate in middle: "12...90"
 * <Truncate text="1234567890" maxChars={4} variant="hide-middle" />
 * ```
 */
export const Truncate = ({
  text,
  maxChars,
  variant,
  typographyVariant,
  tooltipProps,
  typographyProps,
  dataTestId,
}: Props): JSX.Element => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const isTruncated = text.length > maxChars;
  const displayText = useMemo(() => truncateText({ text, maxChars, variant }), [text, maxChars, variant]);

  const handleTooltipOpen = useCallback(() => {
    if (isTruncated) {
      setIsTooltipOpen(true);
    }
  }, [isTruncated]);

  const handleTooltipClose = useCallback(() => {
    setIsTooltipOpen(false);
  }, []);

  const handleClick = useCallback(() => {
    if (isTruncated) {
      setIsTooltipOpen((prev) => !prev);
    }
  }, [isTruncated]);

  // If no truncation needed, just render the text
  if (!isTruncated) {
    return (
      <Typography component="span" variant={typographyVariant} data-testid={dataTestId} {...typographyProps}>
        {text}
      </Typography>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Tooltip
        title={text}
        open={isTooltipOpen}
        onClose={handleTooltipClose}
        onOpen={handleTooltipOpen}
        disableFocusListener
        disableTouchListener
        {...tooltipProps}
      >
        <Box
          component="span"
          onClick={handleClick}
          onMouseEnter={handleTooltipOpen}
          onMouseLeave={handleTooltipClose}
          // eslint-disable-next-line no-restricted-syntax
          sx={{ cursor: 'pointer' }}
          data-testid={dataTestId}
        >
          <Typography
            component="span"
            variant={typographyVariant}
            data-testid={dataTestId ? `${dataTestId}-text` : undefined}
            {...typographyProps}
          >
            {displayText}
          </Typography>
        </Box>
      </Tooltip>
    </ClickAwayListener>
  );
};

export default Truncate;
